import React, {CSSProperties} from 'react';
import styles from '../Home.module.css';

const themeColorsIconUrl = `${process.env.PUBLIC_URL}/icons/theme-colors.svg`;
const greetingIconUrl = `${process.env.PUBLIC_URL}/icons/greeting.svg`;
const editOutlineIconUrl = `${process.env.PUBLIC_URL}/icons/edit-outline.svg`;
const liveFillIconUrl = `${process.env.PUBLIC_URL}/icons/live-fill.svg`;
const videoTwoIconUrl = `${process.env.PUBLIC_URL}/icons/video-two.svg`;
const searchLineIconUrl = `${process.env.PUBLIC_URL}/icons/search-line.svg`;
const userGroupIconUrl = `${process.env.PUBLIC_URL}/icons/user-group.svg`;
const userFillIconUrl = `${process.env.PUBLIC_URL}/icons/user-fill.svg`;
const messageFillIconUrl = `${process.env.PUBLIC_URL}/icons/message-fill.svg`;

const getHeroStatIconUrl = (label: string) => {
  switch (label) {
    case 'Followers':
      return userGroupIconUrl;
    case 'Following':
      return userFillIconUrl;
    case 'Posts':
      return editOutlineIconUrl;
    case 'New Messages':
      return messageFillIconUrl;
    default:
      return userGroupIconUrl;
  }
};

interface HomeHeroStat {
  label: string;
  value: string;
  highlighted?: boolean;
}

interface HomeHeroProps {
  backgroundStyle: CSSProperties;
  userName?: string;
  stats: HomeHeroStat[];
  onCreatePost: () => void;
  onGoLive?: () => void;
  onUploadReel: () => void;
  onDiscoverTalent: () => void;
  showGoLive?: boolean;
  reelActionLabel?: string;
}

const HomeHero: React.FC<HomeHeroProps> = ({
  backgroundStyle,
  userName,
  stats,
  onCreatePost,
  onGoLive,
  onUploadReel,
  onDiscoverTalent,
  showGoLive = false,
  reelActionLabel = 'Upload Reel',
}) => (
  <div className={styles.hero} style={backgroundStyle}>
    <button
      type="button"
      className={styles.themeButton}
      onClick={() => {
        window.location.href = '/settings';
      }}
      title="Change header theme in Settings"
    >
      <img
        src={themeColorsIconUrl}
        alt=""
        className={styles.themeButtonIcon}
        aria-hidden="true"
      />
      <span className={styles.heroButtonIcon}>🎨</span>
      Customize Theme
    </button>

    <div className={`${styles.heroOrb} ${styles.heroOrbPrimary}`} />
    <div className={`${styles.heroOrb} ${styles.heroOrbSecondary}`} />
    <div className={`${styles.heroOrb} ${styles.heroOrbTertiary}`} />

    <div className={`container ${styles.heroInner}`}>
      <div className={styles.heroContent}>
        <h1 className={`${styles.heroTitle} ${styles.heroTitleGreeting}`}>
          <span className={styles.heroTitleText}>Welcome Back, {userName || 'Champion'}!</span>
          <span
            className={styles.heroTitleIcon}
            style={{'--hero-title-icon-url': `url("${greetingIconUrl}")`} as React.CSSProperties}
            aria-hidden="true"
          />
        </h1>
        <h1 className={styles.heroTitle}>Welcome Back, {userName || 'Champion'}! 🚀</h1>
        <p className={styles.heroDescription}>
          Your stage to shine. Connect, create, and inspire.
        </p>

        <div className={styles.heroActions}>
          <button
            type="button"
            className={`${styles.heroButton} ${styles.heroButtonPrimary}`}
            onClick={onCreatePost}
          >
            <span
              className={styles.heroActionIcon}
              style={{'--hero-action-icon-url': `url("${editOutlineIconUrl}")`} as React.CSSProperties}
              aria-hidden="true"
            />
            <span className={styles.heroButtonIcon}>📸</span>
            Create Post
          </button>

          {showGoLive ? (
            <button
              type="button"
              className={`${styles.heroButton} ${styles.heroButtonSecondary} ${styles.heroButtonLive}`.trim()}
              onClick={onGoLive}
            >
              <span
                className={styles.heroActionIcon}
                style={{'--hero-action-icon-url': `url("${liveFillIconUrl}")`} as React.CSSProperties}
                aria-hidden="true"
              />
              <span className={styles.heroButtonIcon}>🔴</span>
              Go Live
            </button>
          ) : null}

          <button
            type="button"
            className={`${styles.heroButton} ${styles.heroButtonSecondary} ${styles.heroButtonUpload}`}
            onClick={onUploadReel}
          >
            <span
              className={styles.heroActionIcon}
              style={{'--hero-action-icon-url': `url("${videoTwoIconUrl}")`} as React.CSSProperties}
              aria-hidden="true"
            />
            <span className={styles.heroButtonIcon}>🎬</span>
            {reelActionLabel}
          </button>

          <button
            type="button"
            className={`${styles.heroButton} ${styles.heroButtonSecondary} ${styles.heroButtonDiscover}`}
            onClick={onDiscoverTalent}
          >
            <span
              className={`${styles.heroActionIcon} ${styles.heroSearchIcon}`}
              style={{'--hero-action-icon-url': `url("${searchLineIconUrl}")`} as React.CSSProperties}
              aria-hidden="true"
            />
            <span className={styles.heroButtonIcon}>🔍</span>
            Discover Talent
          </button>
        </div>

        <div className={styles.heroStats}>
          {stats.map((stat) => (
            <div key={stat.label} className={styles.heroStat}>
              <div
                className={styles.heroStatIcon}
                style={{'--hero-stat-icon-url': `url("${getHeroStatIconUrl(stat.label)}")`} as React.CSSProperties}
                aria-hidden="true"
              />
              <div className={styles.heroStatCopy}>
                <div
                  className={`${styles.heroStatValue} ${stat.highlighted ? styles.heroStatValueHighlighted : ''}`.trim()}
                >
                  {stat.value}
                </div>
                <div className={styles.heroStatLabel}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default React.memo(HomeHero);
